import { format } from "date-fns";
import { useField } from "formik";
import React, { useRef, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useTranslation } from "react-i18next";
import Tooltip from "react-tooltip-lite";
import { capitalizeString } from "../../../../utils/capitalizeString";
import { useOutsideClick } from "../../../../utils/useOutsideClick";
import { Icon } from "../../../Icon/Icon";
import { PasswordErrorMessageTooltip } from "../../PasswordErrorMessageTooltip/PasswordErrorMessageTooltip";
import { EditableInformationTextDisplay } from "../EditableInformation";
import "./EditableInformationCalendar.css";

export const EditableInformationCalendar = ({
  canEdit = true,
  defaultEditingState = false,
  label = "",
  ...props
}) => {
  const { i18n } = useTranslation();
  const ref = useRef();
  const [field, meta, helpers] = useField(props);
  const [displayCalendar, setDisplayCalendar] = useState(false);
  const [editing, setEditing] = useState(defaultEditingState);

  useOutsideClick(ref, (target) => {
    if (displayCalendar && target.name !== "startDate") {
      setDisplayCalendar(false);
    }
  });

  return (
    <div className="EditableInformationTextInput">
      <Tooltip
        useHover={false}
        isOpen={displayCalendar}
        content={
          <div ref={ref}>
            <Calendar
              value={field.value}
              onChange={(newDate) => {
                helpers.setValue(newDate);
                setDisplayCalendar(false);
              }}
              locale={i18n.language || "da"}
              minDetail="month"
              minDate={new Date()}
              formatShortWeekday={(locale, day) => {
                return day
                  .toLocaleDateString(locale, { weekday: "long" })
                  .toUpperCase()
                  .substring(0, 1);
              }}
              tileContent={({ view, date }) => {
                return (
                  <div className="ExportDatepicker__calendar-tile-highlighter">
                    <div className="ExportDatepicker__calendar-tile-text">
                      {view === "month" && date.getDate()}
                      {view === "year" &&
                        capitalizeString(
                          new Intl.DateTimeFormat("da-DK", {
                            month: "long",
                          }).format(date)
                        )}
                    </div>
                  </div>
                );
              }}
              navigationLabel={({ label }) => {
                return capitalizeString(label);
              }}
              nextLabel={<CalendarNextLabel />}
              prevLabel={<CalendarPrevLabel />}
            />
          </div>
        }
      >
        <></>
      </Tooltip>
      {!editing && field.value && (
        <EditableInformationTextDisplay
          value={format(field.value, "dd/MM/yyyy")}
        />
      )}
      {canEdit && editing && (
        <input
          onFocus={() => setDisplayCalendar(true)}
          {...field}
          {...props}
          value={format(field.value, "dd/MM/yyyy")}
          className="EditableInformationTextInput__field"
        />
      )}
      {label && (
        <label
          className={
            meta.value !== ""
              ? "EditableInformationTextInput__label EditableInformationTextInput__label--focused"
              : "EditableInformationTextInput__label"
          }
        >
          {label}
        </label>
      )}
      {meta.touched && meta.error ? (
        <div className="EditableInformationTextInput__error">
          <Tooltip
            padding={0}
            content={
              <PasswordErrorMessageTooltip>
                {meta.error}
              </PasswordErrorMessageTooltip>
            }
          >
            <Icon name="iconNotification" width={20} height={20} />
          </Tooltip>
        </div>
      ) : null}
      {!defaultEditingState && (
        <div className="EditableInformationTextInput__button">
          <div
            className="EditableInformationTextInput__button-toggle"
            onClick={() => setEditing(!editing)}
          >
            {!props.defaultEditingState && (
              <Icon
                name={editing ? "iconCheckmark" : "iconEdit"}
                width={15}
                height={18}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CalendarNextLabel = () => (
  <div className="CalendarNextLabel CalendarLabel">
    <Icon name="arrowDown" width={14} />
  </div>
);

const CalendarPrevLabel = () => (
  <div className="CalendarPrevLabel CalendarLabel">
    <Icon name="arrowDown" width={14} />
  </div>
);
